import { createHead, UnheadProvider } from '@unhead/react/client'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import App from './App.tsx'
import ErrorBoundary from './components/ErrorBoundary'
import Analytics from './components/Analytics'
import './index.css'

const head = createHead()

/**
 * ScrollManager — Emplois BNSSA & MNS
 *
 * Stratégie ROBUSTE pour SPA + mobile (Chrome Android, iOS Safari, bfcache) :
 *
 *  ─ Indexation par PATHNAME (pas location.key)
 *      Les redirects canoniques (ex: /offre/:id → /offres/:slug via <Navigate replace>)
 *      changent la `key` mais représentent la même page logique. En se basant sur
 *      `pathname`, on retrouve toujours la bonne position au retour.
 *
 *  ─ Tracking continu (scroll listener throttlé via rAF)
 *      À chaque pixel scrollé, on stocke `scrollY` pour le pathname courant.
 *      → La position est toujours fraîche au moment de quitter la page.
 *
 *  ─ Persistance via sessionStorage
 *      Permet de survivre au bfcache mobile et aux remontages.
 *
 *  ─ POP (back button) → on RESTAURE la position du pathname cible
 *      Application répétée (rAF, rAF+rAF, setTimeout 0, setTimeout 60, setTimeout 150)
 *      pour battre la scroll restoration native agressive de Chrome Android.
 *
 *  ─ PUSH ou REPLACE avec pathname différent → scrollTo(0,0)
 *      ⚠️ Crucial : `<Navigate replace>` (ex: /offre/:id → /offres/:slug dans JobDetail)
 *      est un REPLACE qui change le pathname → doit aussi remonter en haut.
 *
 *  ─ pageshow (event.persisted) → restauration immédiate depuis sessionStorage
 *      Couvre le cas "retour bfcache" sur Safari iOS et Chrome Android.
 */

const STORAGE_KEY = 'bnssa-mns:scroll-positions'

type PositionMap = Record<string, number>

function loadPositions(): PositionMap {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function savePositions(map: PositionMap) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    // ignore (quota / private mode)
  }
}

function ScrollManager() {
  const { pathname, hash } = useLocation()
  const navType = useNavigationType() // 'POP' | 'PUSH' | 'REPLACE'

  const positionsRef = useRef<PositionMap>(loadPositions())
  const currentPathRef = useRef<string>(pathname)
  const rafIdRef = useRef<number | null>(null)
  const persistTimerRef = useRef<number | null>(null)

  // ─── 1) Désactive scroll restoration native (conflit avec SPA) ──────────────
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      try {
        window.history.scrollRestoration = 'manual'
      } catch {
        // ignore
      }
    }
  }, [])

  // ─── 2) Tracking continu de la position ─────────────────────────────────────
  useEffect(() => {
    const writePosition = () => {
      const y = window.scrollY || window.pageYOffset || 0
      positionsRef.current[currentPathRef.current] = y
      // Persistance différée pour ne pas spammer sessionStorage.
      if (persistTimerRef.current !== null) {
        window.clearTimeout(persistTimerRef.current)
      }
      persistTimerRef.current = window.setTimeout(() => {
        savePositions(positionsRef.current)
        persistTimerRef.current = null
      }, 150)
    }

    const handleScroll = () => {
      if (rafIdRef.current !== null) return
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null
        writePosition()
      })
    }

    const handlePageHide = () => {
      const y = window.scrollY || window.pageYOffset || 0
      positionsRef.current[currentPathRef.current] = y
      savePositions(positionsRef.current)
    }

    // bfcache : à la restauration depuis le cache, on reprend la position.
    const handlePageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return
      const saved = positionsRef.current[currentPathRef.current]
      if (typeof saved === 'number') {
        // Plusieurs tentatives pour battre le scroll natif.
        window.scrollTo(0, saved)
        window.requestAnimationFrame(() => window.scrollTo(0, saved))
        window.setTimeout(() => window.scrollTo(0, saved), 60)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('beforeunload', handlePageHide)
    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('beforeunload', handlePageHide)
      window.removeEventListener('pageshow', handlePageShow)
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
      if (persistTimerRef.current !== null) {
        window.clearTimeout(persistTimerRef.current)
        persistTimerRef.current = null
      }
    }
  }, [])

  // ─── 3) Stratégie de scroll à chaque changement de route ────────────────────
  useEffect(() => {
    const prevPath = currentPathRef.current
    const pathChanged = prevPath !== pathname

    // Avant de basculer, on capture la position actuelle pour le path sortant.
    if (pathChanged) {
      const yNow = window.scrollY || window.pageYOffset || 0
      if (yNow > 0) {
        positionsRef.current[prevPath] = yNow
        savePositions(positionsRef.current)
      }
    }

    currentPathRef.current = pathname

    // Si la cible a une ancre, on laisse le navigateur la gérer.
    if (hash) {
      return
    }

    // Si on reste sur le même pathname (cas REPLACE de canonicalisation
    // sans changement de chemin réel : ex. ajout de query param), on ne
    // touche pas au scroll pour ne pas perturber l'utilisateur.
    if (!pathChanged && navType === 'REPLACE') {
      return
    }

    // Détermine la cible : POP = restaurer ; sinon = top.
    let targetY = 0
    if (navType === 'POP') {
      const saved = positionsRef.current[pathname]
      if (typeof saved === 'number') {
        targetY = saved
      }
    }

    // Application INSISTANTE pour battre la scroll restoration native mobile.
    // Pourquoi 5 tentatives échelonnées ?
    //  - rAF immédiat : après le commit React.
    //  - rAF doublé   : après le 1er repaint.
    //  - setTimeout 0 : après les microtasks.
    //  - setTimeout 60: après le scroll natif Chrome Android (~16-50ms).
    //  - setTimeout 150 : filet de sécurité pour les devices lents.
    const apply = () => {
      try {
        window.scrollTo(0, targetY)
      } catch {
        // ignore
      }
    }

    const timers: number[] = []
    const raf1 = window.requestAnimationFrame(() => {
      apply()
      const raf2 = window.requestAnimationFrame(apply)
      timers.push(raf2)
    })
    timers.push(raf1)
    timers.push(window.setTimeout(apply, 0) as unknown as number)
    timers.push(window.setTimeout(apply, 60) as unknown as number)
    timers.push(window.setTimeout(apply, 150) as unknown as number)

    return () => {
      // Note : on ne distingue pas rAF id et timeout id ici (ranges qui ne
      // se chevauchent pas dans la pratique des navigateurs) — le cleanup
      // est best-effort en cas de re-navigation très rapide.
      timers.forEach((id) => {
        try {
          window.clearTimeout(id)
        } catch {
          // ignore
        }
        try {
          window.cancelAnimationFrame(id)
        } catch {
          // ignore
        }
      })
    }
  }, [pathname, hash, navType])

  return null
}

createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    <UnheadProvider head={head}>
      <BrowserRouter>
        <ScrollManager />
        <Analytics />
        <App />
      </BrowserRouter>
    </UnheadProvider>
  </ErrorBoundary>
)
